import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { tSharedAppState } from 'common/modules/ReactShared';
import * as Template from '../../_pagination';
import { selectors } from './selectors';
import { IItem, tAssignmentsTab } from './types';
import { FETCHERS } from './fetchers';

type tTabState = {
  items: Template.IItems<IItem> | null;
  pagination: Template.IPagination | null;
  params: Template.IParams | null;
  status: 'initial' | 'fetch_loading' | 'received';
  totalCount: number | null;
};

type tAssignmentsState = Record<tAssignmentsTab, tTabState>;

const PER_PAGE = 24;

const createInitialTabState = (): tTabState => ({
  items: null,
  pagination: null,
  params: null,
  status: 'initial',
  totalCount: null
});

const initialState: tAssignmentsState = {
  pending_action: createInitialTabState(),
  waiting_for_review: createInitialTabState(),
  completed: createInitialTabState(),
  failed: createInitialTabState()
};

const getItems = createAsyncThunk<
  {
    tab: tAssignmentsTab;
    items: Template.IItems<IItem>;
    pagination: Template.IPagination;
    params: Template.IParams | null;
    totalCount?: number;
    meta?: { failedCount?: number };
  },
  { tab: tAssignmentsTab; params?: Template.IParams }
>('assignments/getItems_request', async ({ tab, params }) => {
  const fetcher = FETCHERS[tab];
  const noPagination = tab === 'pending_action';
  const response = await fetcher({
    page: params?.page,
    ...(noPagination ? {} : { per_page: params?.perPage ?? PER_PAGE }),
    search: params?.searchValue
  });

  const { records, totalCount, meta } = response;

  const currentPage: number = (params?.page as number) ?? 1;
  const itemsPerPage: number = (params?.perPage as number) ?? PER_PAGE;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Only include totalCount on first load (no params or empty params)
  const isFirstLoad = !params || Object.keys(params).length === 0;

  return {
    tab,
    items: records,
    pagination: {
      currentPage,
      totalPages,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
      prevPage: currentPage > 1 ? currentPage - 1 : null,
      limitValue: itemsPerPage
    },
    params: params ?? null,
    ...(isFirstLoad && { totalCount }),
    meta
  };
});

const getMoreItems = createAsyncThunk<
  {
    tab: tAssignmentsTab;
    succeeded: boolean;
    nextItems?: Template.IItems<IItem>;
    newPagination?: Template.IPagination;
  },
  tAssignmentsTab,
  { state: tSharedAppState }
>('assignments/getMoreItems_request', async (tab, { getState }) => {
  const store = selectors.selectStore(getState());
  const tabState = store[tab];
  const nextPage = tabState.pagination?.nextPage;

  if (!nextPage) return { tab, succeeded: false };

  const fetcher = FETCHERS[tab];
  const response = await fetcher({
    page: nextPage,
    per_page: tabState.params?.perPage ?? PER_PAGE,
    searchValue: tabState.params?.searchValue
  });

  const { records, totalCount } = response;

  const currentPage = nextPage;
  const itemsPerPage: number = (tabState.params?.perPage as number) ?? PER_PAGE;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return {
    tab,
    succeeded: true,
    nextItems: records,
    newPagination: {
      currentPage,
      totalPages,
      nextPage: currentPage < totalPages ? currentPage + 1 : null,
      prevPage: currentPage > 1 ? currentPage - 1 : null,
      limitValue: itemsPerPage
    }
  };
});

const slice = createSlice({
  name: 'assignments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getItems.pending, (state, action) => {
      const { tab } = action.meta.arg;
      state[tab].status = 'fetch_loading';
    });

    builder.addCase(getItems.fulfilled, (state, action) => {
      const { tab, items, pagination, params, totalCount, meta } = action.payload;
      state[tab].items = items;
      state[tab].pagination = pagination;
      state[tab].params = params;
      // Only update totalCount on first load
      if (totalCount !== undefined) {
        state[tab].totalCount = totalCount;
      }
      state[tab].status = 'received';

      // Store failedCount from completed tab response
      if (tab === 'completed' && meta?.failedCount !== undefined) {
        state.failed.totalCount = meta.failedCount;
      }
    });

    builder.addCase(getItems.rejected, (state, action) => {
      const { tab } = action.meta.arg;
      state[tab].status = 'initial';
    });

    builder.addCase(getMoreItems.fulfilled, (state, action) => {
      const { tab, succeeded, nextItems, newPagination } = action.payload;
      if (!succeeded || !newPagination) return;

      const existing = state[tab].items ?? [];
      state[tab].items = existing.concat(nextItems ?? []);
      state[tab].pagination = newPagination;
    });
  }
});

const { reducer } = slice;

const actions = {
  getItems,
  getMoreItems,
  fetchPendingAssignments: (params?: Template.IParams) =>
    getItems({ tab: 'pending_action', params }),
  fetchWaitingForReviewAssignments: (params?: Template.IParams) =>
    getItems({ tab: 'waiting_for_review', params }),
  fetchCompletedAssignments: (params?: Template.IParams) =>
    getItems({ tab: 'completed', params }),
  fetchFailedAssignments: (params?: Template.IParams) =>
    getItems({ tab: 'failed', params })
};

export { reducer, actions };
export type { tAssignmentsState, tTabState, tAssignmentsTab };
